import axios from "axios";
import {ElLoading, ElMessage, ElMessageBox} from "element-plus";
import protobuf from "protobufjs";
import protoRoot, {Framework} from "./proto/proto";
import moment from 'moment-mini';
import {Base32ToDecimal, CreateGuid, strConvertJson, Uint8ArrayToBase64} from "./utils";
import store from "./store";
import {ref} from "vue";

const timeout = 10000;

//请求可选参数
interface RequestOption {
    //基础URL
    baseURL?: string;
    //超时是间
    timeoutTime?: number;
    //是否是下载
    isDownload?: boolean;
    //请求过程是否显示loading校果
    isLoading?: boolean;
    //显示loading时的提示文字，默认：get=请求中... ;post=处理中...
    loadingMsg?: string;
    //是否显示错误信息，默认：true
    showSrvError?: boolean;
}

export function NewDefaultRequestOption() {
    const def: RequestOption = {
        baseURL: undefined,
        timeoutTime: timeout,
        isDownload: undefined,
        isLoading: undefined,
        loadingMsg: undefined,
        showSrvError: true,
    };
    return def;
}

export function ConvertEstate(estate: Framework.Estate) {
    switch (estate) {
        case Framework.Estate.Normal:
            return "正常";
        case Framework.Estate.Stop:
            return "停用";
        case Framework.Estate.Delete:
            return "删除";
    }
}

export function ConvertOperating(value: Framework.Operating) {
    switch (value) {
        case Framework.Operating.Add:
            return "添加";
        case Framework.Operating.Del:
            return "删除";
        case Framework.Operating.Modify:
            return "编辑";
        case Framework.Operating.View:
            return "查看";
        case Framework.Operating.Print:
            return "打印";
        case Framework.Operating.Import:
            return "导入";
        case Framework.Operating.Export:
            return "导出";
        case Framework.Operating.Run:
            return "运行";
        case Framework.Operating.Approve:
            return "审批";
        case Framework.Operating.Register:
            return "注册";
        case Framework.Operating.Login:
            return "登录";
        case Framework.Operating.Logout:
            return "注销";
    }
}

export function BuildRequestToken() {
    const now = moment();
    const reqToken = CreateGuid(now);
    const value = {
        RequestID1: reqToken.substring(0, 15),
        RequestID2: reqToken.substring(15, 29),
        Check: Base32ToDecimal(reqToken.substring(29, 32)),
        Timestamp: now.unix()
    }
    const rt = Framework.RequestToken.create(value);
    const token = Framework.RequestToken.encode(rt).finish();
    return Uint8ArrayToBase64(token);
}

// 是否已显示令牌过期提示 403后续的请求直接忽略
const isShowTokenExpired = ref(false);

axios.interceptors.request.use((request: any) => {
    if (request.baseURL !== "./" && request.baseURL !== "/") {
        const token = store.GetToken();
        if (token) {
            request.headers["X-Token"] = token;
        }
        const platform = store.GetPlatform();
        if (platform) {
            request.headers["X-Platform"] = platform;
        }

        request.headers["X-RequestToken"] = BuildRequestToken();
        if (request.params && request.params.isDownload) {
            request.params.isDownload = undefined;
            request.responseType = "blob";
        } else {
            request.headers["Content-Type"] = "application/x-protobuf";
            request.responseType = "arraybuffer";
            request.transformResponse = transformResponseFactory();
        }
        const mchID = store.GetMchID();
        if (mchID) {
            request.headers["MFK-MchID"] = mchID;
        }
    }

    return request;
});

axios.interceptors.response.use(
    (res) => {
        const {status, data} = res;
        if (res.config.baseURL === "./" || res.config.baseURL === "/") {
            return data;
        }
        // 从 headers 中获取自定义选项
        let requestOption: RequestOption = NewDefaultRequestOption();
        try {
            const optionStr = res.config.headers?.['X-Request-Option'];
            if (optionStr) {
                requestOption = {...NewDefaultRequestOption(), ...JSON.parse(optionStr as string)};
            }
        } catch (e) {
            console.warn('Failed to parse request option:', e);
        }
        if ((status >= 200 && status < 300) || status === 304) {
            if (res.headers["content-type"] === "application/force-download") {
                // 确保 response.data 是 Blob 类型
                if (data instanceof Blob) {
                    const url = window.URL.createObjectURL(data); // 设置路径
                    const link = window.document.createElement("a"); // 创建a标签
                    link.href = window.URL.createObjectURL(data);
                    const filename = res.headers["content-disposition"].split("=")[1];
                    //link.download = decodeURIComponent(res.headers["filename"]); // 设置文件名
                    link.download = decodeURIComponent(filename);
                    link.style.display = "none";
                    link.click();
                    URL.revokeObjectURL(url); // 释放内存
                    return;
                } else {
                    console.error('响应数据不是 Blob 类型');
                }
            }
            if (data.Result) {
                // const token = res.headers["token"];
                // store.SetResponseToken(token)
            } else {
                if (data.Status >= 400 && data.Status < 500)
                    handleRespStatus(data.Status, data.Info)
                if (data.Info) {
                    console.log("axios=>", requestOption);
                    const cancelTip = (data && data.NoTip) || !requestOption.showSrvError;
                    if (!cancelTip) {
                        data.Info = data.Info.replaceAll("]; [", "]｜[");
                        const codes = data.Info.split("｜");
                        let errInfo = "";
                        codes.forEach((code: string) => {
                            errInfo += code;
                        });
                        ElMessage({
                            dangerouslyUseHTMLString: true,
                            message: errInfo,
                            type: "error",
                            showClose: true,
                            duration: 3000
                        });
                    }
                }
            }
            return data;
        } else {
            console.log("else=>", res)
            ElMessage({
                message: "操作失败了,请重新尝试~~",
                type: "error",
                showClose: true,
                duration: 2000
            });
            throw res;
        }
    },
    (err) => {
        let cancelTip;
        console.log("err=>", err)
        if (err && err.config) {
            const reqData = strConvertJson(err.config.data || err.config.params);
            cancelTip = reqData && reqData.NoTip;
        }
        if (err.response)
            handleRespStatus(err.response.status, err.response.data.Info)
        if (!cancelTip) {
            ElMessage({
                message: "操作失败了,请重新尝试！",
                type: "error",
                showClose: true,
                duration: 2000
            });
        }
        throw err;
    }
);

function handleRespStatus(status: number, info: string) {
    switch (status) {
        case 401:
        case 402:
        case 403: {
            store.RemoveToken();
            store.RemoveUserID();
            store.RemoveUserInfo();
            if (!isShowTokenExpired.value) {
                isShowTokenExpired.value = true;
                ElMessageBox.confirm("登录状态已过期，请重新登录", "", {
                    confirmButtonText: "重新登录",
                    showCancelButton: false,
                    type: "warning"
                })
                    .then(() => {
                        doLogin();
                    })
                    .catch(() => {
                        doLogin();
                    });
            }
            return;
        }
        case 410: {
            const oper = Framework.Operating[info as keyof typeof Framework.Operating]
            const msg = `您未获得该模块的 ${ConvertOperating(oper)} 权限`
            ElMessage({
                showClose: true,
                message: msg,
                type: "warning",
                duration: 2000
            });
            return;
        }
    }
}

// 是否是数组
function isArrayBuffer(obj: any) {
    return Object.prototype.toString.call(obj) === "[object ArrayBuffer]";
}

export function protobufLookUp(type: string): any {
    const types = type.split(".")
    let protobufMessage = protoRoot;
    types.forEach((item) => {
        protobufMessage = (protobufMessage as any)[item]
    })
    return protobufMessage
}

// 传输转换
function transformResponseFactory() {
    return function transformResponse(rawResponse: any) {
        // 判断response是否是arrayBuffer
        if (rawResponse == null || !isArrayBuffer(rawResponse)) {
            return rawResponse;
        }
        try {
            const buf = protobuf.util.newBuffer(rawResponse);
            // decode响应体
            const decodedResponse = Framework.Response.decode(buf);
            if (decodedResponse.Data) {
                const type = decodedResponse.Data.type_url?.replace("type.googleapis.com/", "");
                if (type) {
                    const PBMessageResponse = protobufLookUp(type);
                    try {
                        decodedResponse.Data = PBMessageResponse.decode(decodedResponse.Data.value);
                        // const dataValue = PBMessageResponse.decode(decodedResponse.Data.value);
                        // decodedResponse.Data = PBMessageResponse.toObject(dataValue, {
                        //     enums: String,  // enums as string names number?
                        //     longs: String,  // longs as strings (requires long.js)
                        //     bytes: String,  // bytes as base64 encoded strings
                        //     defaults: true, // includes default values
                        //     arrays: true,   // populates empty arrays (repeated fields) even if defaults=false
                        //     objects: true,  // populates empty objects (map fields) even if defaults=false
                        //     oneofs: true    // includes virtual oneof fields set to the present field's name
                        // });
                        // console.log("object:", decodedResponse.Data)
                    } catch (err) {
                        console.error(decodedResponse.Data?.type_url, err, rawResponse, decodedResponse);
                    }
                }
            }
            return decodedResponse;
        } catch (err) {
            console.log(err);
            return err;
        }
    };
}

const safeURL = async (url: string, isMedia?: boolean) => {
    // @ts-ignore
    if (import.meta.env.MODE === "production") {
        await GetConfig();
        if (window.Config.UnUseSafeURL)
            return url;
    } else return url;
    // console.debug(url);
    const now = moment();
    const reqToken = CreateGuid(now);
    const value = {
        Token: {
            RequestID1: reqToken.substring(0, 15),
            RequestID2: reqToken.substring(15, 29),
            Check: Base32ToDecimal(reqToken.substring(29, 32)),
            Timestamp: now.unix()
        },
        URL: url,
        IsMedia: !!isMedia
    }
    const rt = Framework.SafeURL.create(value);
    const urlPB = Framework.SafeURL.encode(rt).finish();
    return encodeURIComponent(Uint8ArrayToBase64(urlPB))
};

/**
 * 获取配置
 */
export async function GetConfig() {
    if ((window.Config && !window.Config.Host) || !window.Config) {
        // const res = await get("", `${import.meta.env.MODE === "production" ? "/admin/" : ""}static/config.yaml`, null, '/');
        const res = await axios({
            url: `static/config.yaml`,
            params: null,
            baseURL: "/"
        });
        if (res) {
            // 使用 yaml 库解析
            const {parse} = await import('yaml'); // 动态导入（可选）或顶部静态导入
            const doc = parse(res.data || res);
            if (doc) {
                if (doc.Host === "127.0.0.1" || doc.Host === "localhost") {
                    doc.Host = window.location.hostname;
                }
                window.Config = doc;
                return doc;
            } else {
                store.RemoveToken();
                return null;
            }
        } else {
            store.RemoveToken();
            return null;
        }
    } else {
        return window.Config;
    }
}

/**
 * GET 获取数据
 * @param apiName
 * @param {string} url          地址路径
 * @param params
 * @param option 可选参数
 */
async function get(
    apiName: string,
    url: string,
    params?: any,
    option?: RequestOption
) {
    if (isShowTokenExpired.value) return null;
    const Config = await GetConfig();
    if (!option) {
        option = NewDefaultRequestOption();
    }
    let baseURL = option.baseURL;
    if (!baseURL) {
        // @ts-ignore
        if (import.meta.env.MODE === "production") {
            baseURL = `${Config.UseSSL ? "https" : "http"}://${Config.Host}:${Config.Port}/${apiName}/`;
        } else {
            baseURL = `${Config.UseSSL ? "https" : "http"}://${Config.Host}:${
                Config.Port
            }/${apiName}${apiName !== "MFKSoft.OPS" ? (Config.DevSuffix ? Config.DevSuffix : "") : ""}/`;
        }
    }
    if (!url.endsWith("static/config.yaml")) url = await safeURL(url);
    if (option.isDownload) params.isDownload = option.isDownload;

    let loadingInstance = undefined;
    if (option.isLoading) {
        loadingInstance = ElLoading.service({
            text: option.loadingMsg ? option.loadingMsg : "请求中..."
        });
    }
    const res = axios({
        method: "get",
        baseURL: baseURL,
        timeout: option.timeoutTime ? option.timeoutTime : Config.Timeout || timeout,
        url: url,
        params: params, // get 请求时带的参数
        headers: {
            'X-Request-Option': JSON.stringify(option)
        }
    });


    if (option.isLoading && loadingInstance) {
        loadingInstance.close();
    }
    return res;
}

export default get

/**
 * POST 获取数据
 * @param apiName
 * @param {string} url          地址路径
 * @param {string} data         提交的数据
 * @param option
 */
export async function post(
    apiName: string,
    url: string,
    data: any,
    option?: RequestOption
) {
    if (isShowTokenExpired.value) return null;
    const Config = await GetConfig();
    if (!option) {
        option = NewDefaultRequestOption();
    }
    let baseURL = option.baseURL;
    if (!baseURL) {
        // @ts-ignore
        if (import.meta.env.MODE === "production") {
            baseURL = `${Config.UseSSL ? "https" : "http"}://${Config.Host}:${Config.Port}/${apiName}/`;
        } else {
            baseURL = `${Config.UseSSL ? "https" : "http"}://${Config.Host}:${
                Config.Port
            }/${apiName}${apiName !== "MFKSoft.OPS" ? (Config.DevSuffix ? Config.DevSuffix : "") : ""}/`;
        }
    }
    url = await safeURL(url);
    const params = {isDownload: false};
    if (option.isDownload) params.isDownload = option.isDownload;

    let loadingInstance = undefined;
    if (option.isLoading) {
        loadingInstance = ElLoading.service({
            text: option.loadingMsg ? option.loadingMsg : "处理中..."
        });
    }

    const res = axios({
        method: "post",
        baseURL: baseURL,
        timeout: option.timeoutTime ? option.timeoutTime : Config.Timeout || timeout,
        url: url,
        data: data, // POST 请求时带的参数
        params
    });
    if (option.isLoading && loadingInstance) {
        loadingInstance.close();
    }
    return res;
}

export async function postPB(apiName: string, url: string, data?: any, option?: RequestOption) {
    if (isShowTokenExpired.value) return;
    const Config = await GetConfig();
    if (!option) {
        option = NewDefaultRequestOption();
    }
    // console.log("postPB=>", data)
    if (!option.baseURL) {
        // @ts-ignore
        if (import.meta.env.MODE === "production") {
            option.baseURL = `${Config.UseSSL ? "https" : "http"}://${Config.Host}:${Config.Port}/${apiName}/`;
        } else {
            option.baseURL = `${Config.UseSSL ? "https" : "http"}://${Config.Host}:${
                Config.Port
            }/${apiName}${apiName !== "MFKSoft.OPS" ? (Config.DevSuffix ? Config.DevSuffix : "") : ""}/`;
        }
    }
    url = await safeURL(url);

    let loadingInstance = undefined;
    if (option.isLoading) {
        loadingInstance = ElLoading.service({
            text: option.loadingMsg ? option.loadingMsg : "处理中..."
        });
    }

    const res = axios({
        method: "post",
        baseURL: option.baseURL,
        timeout: option.timeoutTime ? option.timeoutTime : Config.Timeout || timeout,
        url: url,
        headers: {
            "X-Requested-With": "XMLHttpRequest",
            "Content-Type": "application/x-protobuf",
            Accept: "application/x-protobuf,*/*"
        },
        data: data, // POST 请求时带的参数
        transformRequest: transformRequestFactory()
    });

    if (option.isLoading && loadingInstance) {
        loadingInstance.close();
    }
    return res;
}

// 传输转换
function transformRequestFactory() {
    return function transformRequest(data: any) {
        return ProtobufEncode("google.protobuf.Any", data, false);
    };
}

/**
 * ProtoBuf 编码
 * @param {*} protoType     编码类型
 * @param {*} data          要编码的数据
 * @param {*} needBase64    是否需要转换为Base64字符串
 */
export function ProtobufEncode(protoType: string, data: any, needBase64: any = true) {
    try {
        // 请求体message
        const PBMessageRequest = protobufLookUp(protoType);
        // 将对象序列化成请求体实例
        const reqData = PBMessageRequest.create(data);
        if (protoType.indexOf("List_") !== -1) {
            data.forEach(async (item: any) => {
                reqData.items.push(item);
            });
        }
        // 将请求数据encode成二进制，encode是proto.js提供的方法
        const req = PBMessageRequest.encode(reqData).finish();
        if (needBase64) {
            return window.btoa(String.fromCharCode.apply(null, req));
        }
        return req;
    } catch (err) {
        console.log(err);
        return err;
    }
}

/**
 * ProtoBuf 解码
 * @param data
 * @param type 类型
 * @param isBase64    是否是base64字符串
 * @returns
 */
export function ProtobufDecode(data: any, type: string, isBase64 = true) {
    if (isBase64) {
        const bytes = window.atob(data);
        data = new Uint8Array(bytes.length);
        for (let i = 0; i < bytes.length; i++) {
            data[i] = bytes.charCodeAt(i);
        }
    }
    const PBMessageResponse = protobufLookUp(type);
    return PBMessageResponse.decode(data);
}

/** 跳到登录 */
export async function doLogin() {
    isShowTokenExpired.value = false;
    await GetConfig();
    console.log("doLogin", window.location)
    const isHashMode = window.location.href.includes('#');
    // 判断当前是否为 file:// 协议
    if (window.location.protocol === 'file:') {
        if (isHashMode) {
            window.location.hash = `/#/login?redirect=${encodeURIComponent(location.href)}&origin=Manage`;
        } else {
            (window.location as any) = `/login?redirect=${encodeURIComponent(location.href)}&origin=Manage`;
        }
    } else {
        if (isHashMode) {
            window.location.hash = `http://${window.location.hostname}:${window.location.port}/#/login?redirect=${encodeURIComponent(location.href)}&origin=Manage`;
        } else {
            (window.location as any) = `http://${window.location.hostname}:${window.location.port}/login?redirect=${encodeURIComponent(location.href)}&origin=Manage`;
        }
    }
}


/**
 * 移除登录信息
 */
export function removeLoginInfo() {
    store.RemoveToken();
    store.RemoveUserID();
    store.RemoveUserInfo();
}